import { JsonNetDecycle } from './../../../services/utilities/json-net-decycle';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  ViewChild
} from '@angular/core';
import { of, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { DataService } from '../../../services/data.service';
import { WorkflowService } from '../../../services/workflow.service';
import {
  RequiredDocumentDataEntityInfo,
  RequiredDocumentDataEntitiesResponse,
  SaveDocumentDataEntitiesRequest,
  SaveDocumentDataEntityRequest,
  SaveDocumentDataEntitiesResponse,
  DocumentWithReviewInfo,
  ActivityStatus
} from '../../../models/required-document-data-entity';
import { Document } from '../../../models/document';
import { WorkflowApplicationDocument } from '../../../models/workflow-application-document';
import { ToastrService } from 'ngx-toastr';
import { Utilities, WorkflowContextService } from 'src/app/services';
import { LayoutService } from 'src/app/services/layout.service';
import { svgs } from 'src/assets/export.assets';
import { CommonConfirmationModalData } from '../../common/common-confirmation-modal/common-confirmation-modal.component.model';

@Component({
  selector: 'wm-application-required-documents',
  templateUrl: './application-required-documents.component.html',
  styleUrls: ['./application-required-documents.component.css'],
  standalone: false
})
export class ApplicationRequiredDocumentsComponent
  implements OnInit, OnDestroy {
  @Input() applicationId: string;

  documentEntities: RequiredDocumentDataEntityInfo[] = [];
  generalDocuments: Document[] = []; // Documents not tied to any data entity
  applicationNumber: string = '';
  loading: boolean = false;
  saving: boolean = false;
  ActivityStatus = ActivityStatus;
  templateForPermitForm: boolean = false;
  closeIcon = svgs.closeIcon.name;
  warningAmberIcon = svgs.warningAmberIcon.name;
  infoOutlinedIcon = svgs.infoOutlinedIcon.name;
  requireDocumentWarningText =
    'Some required documents are missing. Please upload all required documents';
  warningRequireText = 'This required document has not been uploaded yet';
  noRequireDocumentText = 'No required documents found for this application.';
  additionalDocumentText = 'Additional Documents';
  reviewDocumentText = 'The uploaded document(s) needs review';
  additionalDocumentSubtext =
    'Upload any additional documents related to this application that are not required by any specific workflow activities.';
  deleteIcon = svgs.deleteAttachmentOutlineIcon.name;
  imageIcon = svgs.driveIcon.name;
  hasUploadChanges: boolean = false;

  hasMissingRequiredDocuments(): boolean {
    return this.documentEntities?.some(
      doc => doc.isRequired && (!doc.documents || doc.documents.length === 0)
    );
  }

  @Output() attachmentsChanged = new EventEmitter<{
    applicationId: string;
    unreadAttachments: number;
    totalAttachments: number;
  }>();

  @Output() close = new EventEmitter<void>();
  private emitAttachmentsSummary(): void {
    const totalAttachments =
      this.generalDocuments.length +
      this.documentEntities.reduce(
        (count, entity) => count + (entity.documents?.length || 0),
        0
      );
    this.attachmentsChanged.emit({
      applicationId: this.applicationId,
      unreadAttachments: 0,
      totalAttachments
    });
  }
  @ViewChild('closeModal', { static: false }) closeModal: any;

  closeModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'You have unsaved changes. Are you sure you want to leave?',
    modalSubDescp: 'If you close this window saving, your changes will be lost',
    yesNoBtn: true,
    replaceYesWith: 'CLOSE',
    replaceNoWith: 'CANCEL'
  };

  closeOpenModal = () => {
    this.closeModalData = {
      ...this.closeModalData,
      open: true
    };
  };

  closeConfirmModal = () => {
    this.closeModalData = {
      ...this.closeModalData,
      open: false
    };
  };

  handleClose(): void {
    this.close.emit();
    const el1 = document.querySelector(
      '.header-parent-container'
    ) as HTMLElement;
    if (el1) {
      el1.style.zIndex = '99';
    }
    this.closeConfirmModal();
  }

  private subscriptions = new Subscription();

  constructor(
    private dataService: DataService,
    private workflowService: WorkflowService,
    private toastr: ToastrService,
    public layoutService: LayoutService,
    public context: WorkflowContextService
  ) {
    layoutService.useNewLayout$.subscribe(useNewLayout => {
      this.templateForPermitForm = useNewLayout;
    });
  }
  isAttachmentOpen = false;

  ngOnInit(): void {
    if (this.applicationId) {
      this.isAttachmentOpen = true;
      this.loadRequiredDocuments();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadRequiredDocuments(): void {
    this.loading = true;
    const sub = this.dataService
      .getApplicationDocumentDataEntities(this.applicationId)
      .subscribe({
        next: (response: RequiredDocumentDataEntitiesResponse) => {
          this.documentEntities = response.documentEntities || [];

          this.documentEntities.forEach(entity => {
            entity.documents?.forEach(doc => {
              const raw = doc as any;
              // Map DocumentWithReviewInfo fields → standard Document fields so the
              // upload component file list, href links, getDocumentFileName(), dedup,
              // and delete operations all work without knowing the backend shape.
              if (!doc.name) doc.name = raw.documentName;
              if (!doc.path) doc.path = raw.documentPath;
              if (!doc.id) doc.id = raw.workflowApplicationDocumentId;
              doc.reviewRequired = entity.requireReview;
              doc.markAsReview = !!doc.reviewedOn;
            });
          });
          this.loadGeneralDocuments();
        },
        error: error => {
          console.error('Error loading required documents:', error);
          this.toastr.error(
            'Failed to load required documents. Please try again.'
          );
          this.loading = false;
        }
      });
    this.subscriptions.add(sub);
  }
  onReviewChanged(workflowApplicationDocumentId: string): void {
    if (this.context?.janusLayout !== 'staff') {
      return;
    }

    this.workflowService
      .markApplicationDocumentAsReviewed(workflowApplicationDocumentId)
      .subscribe({
        next: response => {
          this.hasUploadChanges = false;
          this.toastr.success('Document marked as reviewed.');
        },
        error: () => {
          this.toastr.error('Failed to mark document as reviewed.');
        }
      });
  }

  loadGeneralDocuments(): void {
    // Load all application documents that are NOT tied to a data entity
    const sub = this.workflowService
      .getApplicationDocuments(this.applicationId)
      .subscribe({
        next: (documents: WorkflowApplicationDocument[]) => {
          // Filter for documents without a data entity ID

          const generalDocs = documents.filter(
            d => !d.workflowApplicationDataEntityId && d.documentPath
          );

          // Convert WorkflowApplicationDocument to Document for the upload component
          this.generalDocuments = generalDocs
            .filter(d => d.documentPath)
            .map(d => {
              const doc = this.convertToDocument(d);

              doc.reviewRequired = d.reviewRequired;
              doc.reviewedBy = d.reviewedBy;
              doc.reviewedOn = d.reviewedOnString;

              doc.markAsReview = !!d.reviewedOnString && !!d.reviewedBy;
              this.hasUploadChanges = false;

              return doc;
            });

          if (this.templateForPermitForm) {
            this.emitAttachmentsSummary();
          }
          this.loading = false;
        },
        error: error => {
          console.error('Error loading general documents:', error);
          this.toastr.error('Failed to load additional documents.');
          this.loading = false;
        }
      });
    this.subscriptions.add(sub);
  }

  private convertToDocument(appDoc: WorkflowApplicationDocument): Document {
    return new Document({
      id: appDoc.id,
      name: appDoc.documentName,
      path: appDoc.documentPath,
      parentID: appDoc.workflowApplicationId,
      parentType: 'application',
      parentPath: '',
      tokenPath: '',
      metaData: {},
      documentName: appDoc.documentName,
      workflowAplicationId: appDoc.workflowApplicationId
    });
  }

  // General document handlers
  onGeneralUploadComplete(documents: Document[]): void {
    if (documents && documents.length > 0) {
      this.hasUploadChanges = true;
      const merged = [...this.generalDocuments, ...documents];

      this.generalDocuments = merged.filter(
        (doc, index, self) => index === self.findIndex(d => d.id === doc.id)
      ); //general document gets duplicate so filtering that

      if (this.templateForPermitForm) {
        this.emitAttachmentsSummary();
      }
      this.toastr.success(
        `${documents.length} additional document(s) uploaded successfully`,
        'Upload Complete'
      );
    }
  }

  onGeneralFileDeleted(document: Document): void {
    const index = this.generalDocuments.findIndex(d => d.id === document.id);
    if (index > -1) {
      this.generalDocuments.splice(index, 1);
                  this.hasUploadChanges = false;
      if (this.templateForPermitForm) {
        this.emitAttachmentsSummary();
      }
      this.toastr.info('Document removed');
    }
  }

  getGeneralUploadPathSegments(): string[] {
    return [this.applicationId, 'general'];
  }

  getGeneralUploadKey(): string {
    return `${this.applicationId}_general`;
  }

  onUploadComplete(
    documents: Document[],
    docEntity: RequiredDocumentDataEntityInfo
  ): void {
    if (documents && documents.length > 0) {
      this.hasUploadChanges = true;
      const merged = [...(docEntity.documents || []), ...documents];
      docEntity.documents = merged.filter(
        (doc, index, self) => index === self.findIndex(d => d.id === doc.id)
      );
      if (this.templateForPermitForm) {
        this.emitAttachmentsSummary();
      }
      this.toastr.success(
        `${documents.length} document(s) uploaded successfully to ${docEntity.label}`,
        'Upload Complete'
      );
    }
  }

  onFileDeleted(
    docEntity: RequiredDocumentDataEntityInfo,
    document: Document
  ): void {
    const index = docEntity.documents.findIndex(d => d.id === document.id);
    if (index > -1) {
      docEntity.documents.splice(index, 1);
          this.hasUploadChanges = false;

      if (this.templateForPermitForm) {
        this.emitAttachmentsSummary();
      }
      this.toastr.info('Document removed');
    }
  }

  getUploadPathSegments(docEntity: RequiredDocumentDataEntityInfo): string[] {
    return [this.applicationId, docEntity.activityId, docEntity.templateCode];
  }

  getUploadKey(docEntity: RequiredDocumentDataEntityInfo): string {
    return `${docEntity.activityId}_${docEntity.templateCode}`;
  }

  saveDocuments(): void {
    if (!this.hasUploadChanges) {
      this.toastr.warning('No documents to upload.');
      this.saving = false;
      return;
    }

    this.saving = true;

    // Build request for document data entities — only include entities the current user can upload to
    const documentDataEntities: SaveDocumentDataEntityRequest[] = this.documentEntities
      .filter(de => de.canUpload && de.documents && de.documents.length > 0)
      .map(de => ({
        applicationId: this.applicationId,
        activityId: de.activityId,
        activityDataId: de.activityDataId,
        templateCode: de.templateCode,
        documents: JsonNetDecycle.decycle(
          de.documents.map(doc => {
            const raw = doc as any;
            return new DocumentWithReviewInfo({
              documentName: raw.documentName || doc.name,
              documentPath: raw.documentPath || doc.path,
              workflowApplicationDocumentId: raw.workflowApplicationDocumentId,
              isReviewed: raw.isReviewed ?? false,
              reviewedBy: doc.reviewedBy,
              reviewedOnString: raw.reviewedOnString
            });
          })
        )
      }));

    // Build general documents (not tied to data entities).
    // Only include NEW uploads (no workflowAplicationId means the doc came from an UploadResponse
    // and has never been registered as a WorkflowApplicationDocument record). Pre-existing DB records
    // already have a WfAppDoc ID and must not be re-submitted — doing so sends the storage file ID
    // as the lookup key, which the backend treats as a new record and creates a placeholder with
    // WorkflowApplicationDataEntityId = null.
    const generalApplicationDocs: WorkflowApplicationDocument[] = this.generalDocuments
      .filter(doc => !doc.workflowAplicationId)
      .map(
        doc =>
          new WorkflowApplicationDocument({
            id: '00000000-0000-0000-0000-000000000000',
            workflowApplicationId: this.applicationId,
            workflowApplicationDataEntityId: null,
            workflowApplicationActivityDataId: null,
            documentName: doc.documentName || doc.name,
            documentPath: doc.path,
            uploadedBy: null,
            uploadedOn: new Date().toISOString()
          })
      );

    if (
      documentDataEntities.length === 0 &&
      generalApplicationDocs.length === 0
    ) {
      this.toastr.warning('No documents to save.');
      this.saving = false;
      return;
    }

    // Step 1: save document data entities (if any), then step 2: save general docs sequentially via switchMap
    const saveEntities$ =
      documentDataEntities.length > 0
        ? this.dataService
            .saveDocumentDataEntities(
              new SaveDocumentDataEntitiesRequest({
                applicationId: this.applicationId,
                documentDataEntities
              })
            )
            .pipe(
              tap((response: SaveDocumentDataEntitiesResponse) => {
                if (!response.success) {
                  throw new Error(
                    response.message || 'Failed to save documents.'
                  );
                }
                this.toastr.success(response.message);
                if (response.processedActivities?.length > 0) {
                  this.toastr.info(
                    `The following activities were automatically processed: ${response.processedActivities.join(
                      ', '
                    )}`,
                    'Activities Processed',
                    { timeOut: 8000 }
                  );
                }
              })
            )
        : of(null);

    const sub = saveEntities$
      .pipe(
        switchMap(() =>
          generalApplicationDocs.length > 0
            ? this.workflowService
                .saveWorkflowApplicationDocuments(
                  this.applicationId,
                  generalApplicationDocs
                )
                .pipe(
                  tap(savedDocs => {
                    //filter the data which having documentName and documentPath
                    const uploadedDocs = savedDocs.filter(
                      d => d.documentName && d.documentPath
                    );

                    this.toastr.success(
                      `${uploadedDocs.length} additional document(s) saved successfully`
                    );
                  })
                )
            : of(null)
        )
      )
      .subscribe({
        next: () => {
          this.hasUploadChanges = false;
          this.loadRequiredDocuments();
          this.saving = false;
        },
        error: err => {
          console.error('Error saving documents:', err);
          this.toastr.error(
            err?.message || 'Failed to save documents. Please try again.'
          );
          this.hasUploadChanges = false;
          this.saving = false;
        }
      });

    this.subscriptions.add(sub);
  }

  getStatusLabel(status: ActivityStatus): string {
    switch (status) {
      case ActivityStatus.NotStarted:
        return 'Not Started';
      case ActivityStatus.InProgress:
        return 'In Progress';
      case ActivityStatus.Completed:
        return 'Completed';
      case ActivityStatus.Canceled:
        return 'Canceled';
      default:
        return 'Unknown';
    }
  }

  getStatusClass(status: ActivityStatus): string {
    switch (status) {
      case ActivityStatus.NotStarted:
        return 'status-not-started';
      case ActivityStatus.InProgress:
        return 'status-in-progress';
      case ActivityStatus.Completed:
        return 'status-completed';
      case ActivityStatus.Canceled:
        return 'status-canceled';
      default:
        return '';
    }
  }

  hasIncompleteRequiredDocuments(): boolean {
    return this.documentEntities.some(
      de =>
        de.isRequired &&
        (!de.documents || de.documents.length === 0) &&
        de.activityStatus !== ActivityStatus.Completed
    );
  }

  getDocumentFileName(doc: Document): string {
    return doc.name || doc.path?.split('/').pop() || 'Unknown';
  }
  validGeneralDocumentTypes = '.pdf,.jpeg,.jpg,.xls,.doc,.docx,.xlsx,.txt';
  @ViewChild('attachmentContent')
  attachmentContent: any;

  scrollToTop(): void {
    if (this.attachmentContent) {
      this.attachmentContent.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }
  getStatusJanusClass(status: ActivityStatus): string {
    switch (status) {
      case ActivityStatus.NotStarted:
        return 'janus-status-not-started';
      case ActivityStatus.InProgress:
        return 'janus-status-in-progress';
      case ActivityStatus.Completed:
        return 'janus-status-completed';
      case ActivityStatus.Canceled:
        return 'janus-status-canceled';
      default:
        return '';
    }
  }
  @ViewChild('saveModal', { static: false }) saveModal: any;

  saveModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Are you sure you want to save the uploaded documents?',
    modalSubDescp:
      'Saving these files will attach the uploaded documents to this request',
    yesNoBtn: true,
    replaceYesWith: 'SAVE',
    replaceNoWith: 'CANCEL'
  };

  saveOpenModal = () => {
    this.saveModalData = {
      ...this.saveModalData,
      open: true
    };
  };
  saveCloseModal = () => {
    this.saveModalData = {
      ...this.saveModalData,
      open: false
    };
  };
  saveJanusDocuments = () => {
    this.saveDocuments();
    this.saveCloseModal();
  };
}
